/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Client
 */

import Keycloak from 'keycloak-js';
import _ from 'lodash';
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import log from 'loglevel';
import EventTracker from 'utils/eventTracker';

import config from 'configs/app';

const NETWORK_ERROR_MESSAGE = 'Network Error';

import {
	InitialSetupInformationApi,
	ProvidersApi,
	ServiceLocationDetailsApi,
	ServiceTypesApi,
	PayersApi,
	EzapiApi,
	Configuration,
	SettingsApi,
	KeycloakUserApi,
	AuditApi,
	CustomOutputApi,
	FeaturesApi,
	ManageAccountAndSubscriptionsApi,
	EKYCServiceApi,
	AddOnsApi,
	EZAPIPayersApi,
	BlobDataApi,
	WorkflowApi,
	WorklistApi,
	WorklistEntityApi,
	FHIRClaimApi,
	FHIRClaimResponseApi,
	CodeExplorerApi,
	CodeExplorerTenantDataApi,
	PatientApi,
	ActivityLogApi,
	ActivityTypeApi,
	FHIRPatientApi,
	FHIRCoverageApi,
	TenantPayerApi,
	FHIRAccountApi,
	FHIRPaymentReconciliationApi,
	AttachmentApi,
	AnalyticsApi,
	NoteApi,
	ClearingHouseApi,
	EntitySettingsApi,
	WriteOffReasonApi,
	StatementGenerationApi,
	CustomAttributesApi,
	CustomAttributeValuesApi,
	FHIREncounterApi,
	FHIRChargeItemApi,
} from '@veritable/common/adminApi';

// import { AddOnsApi as AccountsAddOnsInfoApi } from '@veritable/common/accountsApi';
// TODO : remove later
import AccountsAddOnsInfoApi from './AccountsMock';

const MESSAGE = '[AxiosInterceptor]';

/**
 * Axios Client
 */
class AxiosClient {
	minValidity!: number;
	keycloak!: Keycloak;

	/**
	 * Web apis
	 */
	FeaturesApi!: FeaturesApi;
	Subscriptions!: ManageAccountAndSubscriptionsApi;
	AddOns!: AddOnsApi;
	AnalyticsApi!: AnalyticsApi;
	AuditApi!: AuditApi;
	BlobDataApi!: BlobDataApi;
	CodeExplorerApi!: CodeExplorerApi;
	CodeExplorerTenantDataApi!: CodeExplorerTenantDataApi;
	CustomOutputApi!: CustomOutputApi;
	EzapiApi!: EzapiApi;
	InitialSetupInformationApi!: InitialSetupInformationApi;
	KeycloakUserApi!: KeycloakUserApi;
	PayersApi!: PayersApi;
	ProvidersApi!: ProvidersApi;
	ServiceLocationDetailsApi!: ServiceLocationDetailsApi;
	ServiceTypesApi!: ServiceTypesApi;
	SettingsApi!: SettingsApi;
	EKYCServiceApi!: EKYCServiceApi;
	EZAPIPayersApi!: EZAPIPayersApi;
	WorkflowApi!: WorkflowApi;
	WorklistApi!: WorklistApi;
	WorklistEntityApi!: WorklistEntityApi;
	FHIRClaimApi!: FHIRClaimApi;
	PatientApi!: PatientApi;
	ActivityLogApi!: ActivityLogApi;
	ActivityTypeApi!: ActivityTypeApi;
	FHIRClaimResponseApi!: FHIRClaimResponseApi;
	FHIRPatientApi!: FHIRPatientApi;
	FHIRCoverageApi!: FHIRCoverageApi;
	TenantPayerApi!: TenantPayerApi;
	FHIRAccountApi!: FHIRAccountApi;
	AttachmentApi!: AttachmentApi;
	PaymentReconciliationApi!: FHIRPaymentReconciliationApi;
	NoteApi!: NoteApi;
	ClearingHouseApi!: ClearingHouseApi;
	EntitySettingsApi!: EntitySettingsApi;
	WriteOffReasonApi!: WriteOffReasonApi;
	StatementGenerationApi!: StatementGenerationApi;
	CustomAttributesApi!: CustomAttributesApi;
	CustomAttributeValuesApi!: CustomAttributeValuesApi;
	FHIREncounterApi!: FHIREncounterApi;
	FHIRChargeItemApi!: FHIRChargeItemApi;
	/**
	 * Addons subscriptions apis
	 */
	AccountsAddOnsInfoApi!: AccountsAddOnsInfoApi;

	init = (keycloak: Keycloak, minValidity = 5): void => {
		this.keycloak = keycloak;
		this.minValidity = minValidity;

		this.webClientCreation();
		this.accountsClientCreation();
	};

	/**
	 * Create the web client
	 */
	private readonly webClientCreation = () => {
		const client = axios.create(config.client);
		const basePath = config.client.baseURL;
		client.interceptors.request.use(this.requestInterceptor, this.rejectInterceptor);
		client.interceptors.response.use(this.responseInterceptor, this.rejectInterceptor);

		this.FeaturesApi = new FeaturesApi(undefined, basePath, client);
		this.Subscriptions = new ManageAccountAndSubscriptionsApi(undefined, basePath, client);
		this.AddOns = new AddOnsApi(undefined, basePath, client);

		this.InitialSetupInformationApi = new InitialSetupInformationApi(undefined, basePath, client);
		this.KeycloakUserApi = new KeycloakUserApi(undefined, basePath, client);
		this.ProvidersApi = new ProvidersApi(undefined, basePath, client);
		this.EzapiApi = new EzapiApi(new Configuration(), basePath, client);
		this.PayersApi = new PayersApi(undefined, basePath, client);
		this.ServiceLocationDetailsApi = new ServiceLocationDetailsApi(undefined, basePath, client);
		this.ServiceTypesApi = new ServiceTypesApi(undefined, basePath, client);
		this.SettingsApi = new SettingsApi(undefined, basePath, client);
		this.KeycloakUserApi = new KeycloakUserApi(undefined, basePath, client);
		this.AuditApi = new AuditApi(undefined, basePath, client);
		this.CustomOutputApi = new CustomOutputApi(undefined, basePath, client);
		this.EKYCServiceApi = new EKYCServiceApi(undefined, basePath, client);
		this.EZAPIPayersApi = new EZAPIPayersApi(undefined, basePath, client);
		this.BlobDataApi = new BlobDataApi(undefined, basePath, client);
		this.WorkflowApi = new WorkflowApi(undefined, basePath, client);
		this.WorklistApi = new WorklistApi(undefined, basePath, client);
		this.WorklistEntityApi = new WorklistEntityApi(undefined, basePath, client);
		this.FHIRClaimApi = new FHIRClaimApi(undefined, basePath, client);
		this.FHIRClaimResponseApi = new FHIRClaimResponseApi(undefined, basePath, client);
		this.CodeExplorerApi = new CodeExplorerApi(undefined, basePath, client);
		this.CodeExplorerTenantDataApi = new CodeExplorerTenantDataApi(undefined, basePath, client);
		this.PatientApi = new PatientApi(undefined, basePath, client);
		this.ActivityLogApi = new ActivityLogApi(undefined, basePath, client);
		this.ActivityTypeApi = new ActivityTypeApi(undefined, basePath, client);
		this.FHIRPatientApi = new FHIRPatientApi(undefined, basePath, client);
		this.FHIRCoverageApi = new FHIRCoverageApi(undefined, basePath, client);
		this.TenantPayerApi = new TenantPayerApi(undefined, basePath, client);
		this.PaymentReconciliationApi = new FHIRPaymentReconciliationApi(undefined, basePath, client);
		this.AttachmentApi = new AttachmentApi(undefined, basePath, client);
		this.FHIRAccountApi = new FHIRAccountApi(undefined, basePath, client);
		this.AnalyticsApi = new AnalyticsApi(undefined, basePath, client);
		this.NoteApi = new NoteApi(undefined, basePath, client);
		this.ClearingHouseApi = new ClearingHouseApi(undefined, basePath, client);
		this.EntitySettingsApi = new EntitySettingsApi(undefined, basePath, client);
		this.WriteOffReasonApi = new WriteOffReasonApi(undefined, basePath, client);
		this.StatementGenerationApi = new StatementGenerationApi(undefined, basePath, client);
		this.CustomAttributesApi = new CustomAttributesApi(undefined, basePath, client);
		this.CustomAttributeValuesApi = new CustomAttributeValuesApi(undefined, basePath, client);
		this.FHIREncounterApi = new FHIREncounterApi(undefined, basePath, client);
		this.FHIRChargeItemApi = new FHIRChargeItemApi(undefined, basePath, client);
	};

	/**
	 * Create the accounts client
	 */
	private readonly accountsClientCreation = () => {
		const client = axios.create(config.accountsClient);
		const basePath = config.accountsClient.baseURL;

		client.interceptors.request.use(undefined, this.rejectInterceptor);
		client.interceptors.response.use(this.responseInterceptor, this.rejectInterceptor);

		this.AccountsAddOnsInfoApi = new AccountsAddOnsInfoApi(undefined, basePath, client);
	};

	/**
	 * Request interceptor
	 * @param requestConfig - Request config
	 * @returns Request config
	 */
	requestInterceptor = async (requestConfig: InternalAxiosRequestConfig<AnyType>): Promise<InternalAxiosRequestConfig<AnyType>> => {
		try {
			await this.keycloak.updateToken(this.minValidity);
			if (_.isString(this.keycloak.token)) {
				_.setWith(requestConfig, 'headers.Authorization', `Bearer ${this.keycloak.token}`, Object);
			}
			return requestConfig;
		} catch (ex) {
			log.error(MESSAGE, ex);
			this.keycloak.clearToken();
			return Promise.reject(requestConfig);
		}
	};

	/**
	 * Response interceptor
	 * @param response - Response
	 * @returns Response
	 */
	responseInterceptor = (
		response: AxiosResponse<AnyType, AnyType>,
	): Promise<AxiosResponse<AnyType, AnyType>> | AxiosResponse<AnyType, AnyType> => {
		// According to default config
		// Response interceptor will only be called b/w status code >=200 to < 300
		return response;
	};

	/**
	 * Reject interceptor
	 * @param error - Error
	 * @returns Error
	 */
	rejectInterceptor = async (error: AxiosError): Promise<never> => {
		if (error?.message !== NETWORK_ERROR_MESSAGE) {
			EventTracker.trackError(error);
		}

		if (error.response) {
			log.error(MESSAGE, error.response.status);
			switch (error.response.status) {
				case 400:
					log.error('[AxiosInterceptor] Bad Request');
					break;
				case 401:
					log.error('[AxiosInterceptor] UnAuthorized');
					break;
				case 403:
					log.error('[AxiosInterceptor] Forbidden');
					break;
				case 404:
					log.error('[AxiosInterceptor] Not Found');
					break;
			}
		}
		return Promise.reject(error);
	};
}

export default AxiosClient;
